// src/shared/components/layout/Footer.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Instagram } from 'lucide-react';
import logo from '../../../assets/images/logo/pontso-yarona-logo.png';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const pillars = [
    {
      name: 'Kganya Scents',
      link: '/kganya-scents',
      description: 'Luxury scents and sensory rituals'
    },
    {
      name: 'Farming',
      link: '/farming',
      description: 'Natural ingredients and ethical sourcing'
    },
    {
      name: 'Films',
      link: '/films',
      description: 'Storytelling and cinematic artistry'
    }
  ];

  const quickLinks = [
    { name: 'About Us', link: '/#about' },
    { name: 'Leadership', link: '/#founders' },
    { name: 'Contact', link: '/#contact' },
  ];

  const socialLinks = [
    { icon: Instagram, name: 'Instagram', url: 'https://www.instagram.com/pontso_yarona/' },
  ];

  const contactInfo = {
    phone: '+27 84 312 9759',
    email: 'info@pontsoyarona.co.za',
    address: 'Johannesburg, Gauteng'
  };

  return (
    <footer className="bg-brand-black text-brand-light border-t border-brand-red/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-3 mb-6">
              <img 
                src={logo} 
                alt="Pontso Yarona Logo" 
                className="h-12 w-auto drop-shadow"
              />
              <div>
                <h3 className="text-lg font-heading font-semibold text-brand-gold">
                  Pontso Yarona
                </h3>
                <p className="text-xs text-brand-light/80 font-body font-light tracking-wide">
                  Empires of the mind
                </p>
              </div>
            </Link>
            <p className="text-brand-light/70 font-body font-light text-sm leading-relaxed">
              Building sensory-led businesses across Kganya Scents, Farming, and Films.
            </p>
          </div>

          {/* Pillars Column */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-base text-brand-gold">
              Our Worlds
            </h4>
            <ul className="space-y-3">
              {pillars.map((pillar) => (
                <li key={pillar.name}>
                  <Link
                    to={pillar.link}
                    className="text-brand-light/80 hover:text-brand-gold font-body font-light text-sm transition-colors duration-300 flex items-center group"
                  >
                    <span>{pillar.name}</span>
                    <span className="ml-2 text-brand-red/70 group-hover:text-brand-red transition-colors">
                      →
                    </span>
                  </Link>
                  <p className="text-brand-light/60 text-xs font-body font-light mt-1">
                    {pillar.description}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links Column */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-base text-brand-gold">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.link}
                    className="text-brand-light/80 hover:text-brand-gold font-body font-light text-sm transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-base text-brand-gold">
              Get in Touch
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:+27843129759"
                  className="flex items-center text-brand-light/80 hover:text-brand-gold font-body font-light text-sm transition-colors duration-300 group"
                >
                  <Phone className="w-4 h-4 mr-3 text-brand-gold/80 group-hover:text-brand-gold transition-colors" />
                  {contactInfo.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center text-brand-light/80 hover:text-brand-gold font-body font-light text-sm transition-colors duration-300 group"
                >
                  <Mail className="w-4 h-4 mr-3 text-brand-gold/80 group-hover:text-brand-gold transition-colors" />
                  {contactInfo.email}
                </a>
              </li>
              <li>
                <div className="flex items-start text-brand-light/80 font-body font-light text-sm">
                  <MapPin className="w-4 h-4 mr-3 text-brand-gold/80 mt-0.5 flex-shrink-0" />
                  {contactInfo.address}
                </div>
              </li>
            </ul>

            {/* Social Links */}
            <div className="mt-6">
              <h5 className="text-brand-light font-body font-medium text-sm mb-3">
                Follow Us
              </h5>
              <div className="flex space-x-4">
                {socialLinks.map((social) => {
                  const IconComponent = social.icon;
                  return (
                    <a
                      key={social.name}
                      href={social.url}
                      className="w-10 h-10 bg-brand-black border border-brand-gold/30 hover:border-brand-red rounded-lg flex items-center justify-center transition-colors duration-300 group"
                      aria-label={social.name}
                    >
                      <IconComponent className="w-5 h-5 text-brand-light/70 group-hover:text-brand-gold transition-colors" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-brand-red/20 mt-12 pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="text-brand-light/70 font-body font-light text-sm text-center sm:text-left">
              © {currentYear} Pontso Yarona Group. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link
                to="/"
                className="text-brand-light/70 hover:text-brand-gold font-body font-light text-sm transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/"
                className="text-brand-light/70 hover:text-brand-gold font-body font-light text-sm transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

